import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { NAVIGATION_KEYS } from '@features/navigation/navigation.query-keys';
import { IDENTITY_KEYS } from '../identity.query-keys';
import { AuthService } from './auth.service';

const PUBLIC_PATH_PREFIXES = [
    '/iniciar-sesion',
    '/olvide-contrasena',
    '/restablecer-contrasena',
    '/acceso-denegado',
    '/error',
];

/**
 * Reacts to a server-side session / CSRF expiry detected on any HTTP call: drops the cached auth
 * state so the route guards stop trusting it, then bounces to the login screen — no manual reload.
 * Safe to call from many failing requests at once; only the first one does the work.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryService {
    readonly #router = inject(Router);
    readonly #queryClient = inject(QueryClient);
    readonly #auth = inject(AuthService);

    #handling = false;

    async expire(): Promise<void> {
        if (this.#handling) return;
        // Still bootstrapping: the blocking app initializer resolves the session and the guards
        // redirect from there — navigating now would race that.
        if (!this.#router.navigated) return;
        // Already on a public screen (e.g. the login page polling its own session): nothing to do.
        if (PUBLIC_PATH_PREFIXES.some((p) => this.#router.url.startsWith(p))) return;

        this.#handling = true;
        try {
            // Flip isAuthenticated() to false right away. A failed *refetch* otherwise keeps the
            // stale user in cache with status 'success', so noAuthGuard bounces /iniciar-sesion
            // straight back into the app.
            this.#queryClient.setQueryData(IDENTITY_KEYS.session(), null);
            this.#queryClient.removeQueries({ queryKey: NAVIGATION_KEYS.all() });
            // Refresh the XSRF cookie so the login POST that follows isn't rejected in turn.
            await lastValueFrom(this.#auth.csrf()).catch(() => undefined);
            await this.#router.navigate(['/iniciar-sesion']);
        } finally {
            this.#handling = false;
        }
    }
}
